import { Feature, Point } from 'geojson';
import mapboxgl, { Map, Marker } from 'mapbox-gl';
import { hideMarkerAtZoomLevel } from './hide_marker_at_zoom';
import trophyIcon from '../../images/icons/trophy.svg';

export type AchievementFeature = Feature<
  Point,
  {
    distanceValue: string | number;
    distanceUnitLabel: string;
    position: number;
    thumbnail: string;
    href: string;
    id: string;
  }
>;

export type RemainingAchievementFeature = Feature<
  Point,
  { thumbnail: string; id: string }
>;

export const addRemainingAchievementMarker = (
  map: Map,
  feature: RemainingAchievementFeature,
  markers: Marker[]
): void => {
  const el = document.createElement('div');
  const innerEl = document.createElement('div');
  const wrapperEl = document.createElement('div');

  el.className = 'remaining-achievement-marker';
  innerEl.className = 'remaining-achievement-marker__inner';
  innerEl.innerText = '?';
  innerEl.style.backgroundImage = `url("${feature.properties.thumbnail}")`;
  wrapperEl.appendChild(el);
  el.appendChild(innerEl);

  const marker = new mapboxgl.Marker(wrapperEl)
    .setLngLat([
      feature.geometry.coordinates[0],
      feature.geometry.coordinates[1]
    ])
    .addTo(map);

  hideMarkerAtZoomLevel(map, marker);
  markers.push(marker);
};

export const addAchievementMarker = (
  map: Map,
  feature: AchievementFeature,
  markers: Marker[],
  roundDistance = false
): void => {
  const el = document.createElement('a');
  const innerEl = document.createElement('div');
  const wrapperEl = document.createElement('div');
  const achievementThumbnail = document.createElement('img');
  const achievementTitle = document.createElement('h1');
  const achievementIcon = document.createElement('img');
  const achievementSubtitle = document.createElement('h2');

  achievementIcon.src = trophyIcon;
  achievementTitle.className =
    'achievement-marker__title display-4 d-flex gap-1 align-items-center';
  achievementTitle.appendChild(achievementIcon);
  achievementTitle.appendChild(
    document.createTextNode(feature.properties.position.toString())
  );

  const distanceValue = document.createElement('strong');
  const distanceText = roundDistance
    ? Math.round(Number(feature.properties.distanceValue)).toString()
    : String(feature.properties.distanceValue);

  distanceValue.appendChild(document.createTextNode(distanceText));
  achievementSubtitle.className = 'achievement-marker__subtitle fw-normal';
  achievementSubtitle.appendChild(distanceValue);
  achievementSubtitle.appendChild(
    document.createTextNode(` ${feature.properties.distanceUnitLabel}`)
  );

  el.className = 'achievement-marker';
  achievementThumbnail.src = feature.properties.thumbnail;
  achievementThumbnail.className = 'achievement-marker__thumbnail';
  innerEl.className = 'achievement-marker__inner';
  el.href = feature.properties.href;

  if (!document.body.classList.contains('native')) {
    el.dataset.turboFrame = 'modal';
  }

  innerEl.appendChild(achievementTitle);
  innerEl.appendChild(achievementThumbnail);
  innerEl.appendChild(achievementSubtitle);
  wrapperEl.appendChild(el);
  el.appendChild(innerEl);

  const marker = new mapboxgl.Marker(wrapperEl)
    .setLngLat([
      feature.geometry.coordinates[0],
      feature.geometry.coordinates[1]
    ])
    .addTo(map);

  hideMarkerAtZoomLevel(map, marker);
  markers.push(marker);
};

export const addAchievementMarkersFromCollection = (
  map: Map,
  features: Feature[],
  markers: Marker[],
  roundDistance = false
): void => {
  const remaining = features.filter(
    feature =>
      feature.properties?.variant === 'remaining-achievement' &&
      feature.geometry.type === 'Point'
  ) as RemainingAchievementFeature[];

  for (const feature of remaining) {
    addRemainingAchievementMarker(map, feature, markers);
  }

  const earned = features
    .filter(
      feature =>
        feature.properties?.variant === 'achievement' &&
        feature.geometry.type === 'Point'
    )
    .reverse() as AchievementFeature[];

  for (const feature of earned) {
    addAchievementMarker(map, feature, markers, roundDistance);
  }
};
